import { useState } from "react";
import { useReportFinancial, useReportCashflow, useReportRisk, useAdminMembers } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import StatCard from "../../components/ui/StatCard";
import MiniBarChart from "../../components/charts/MiniBarChart";
import RiskMeter from "../../components/ui/RiskMeter";
import Select from "../../components/ui/Select";

const fmt = (n:number) => new Intl.NumberFormat("en-NG",{style:"currency",currency:"NGN", currencyDisplay:"symbol",maximumFractionDigits:0}).format(n);

export default function ReportsPage() {
  const [tab,    setTab]    = useState("financial");
  const [period, setPeriod] = useState("may-2025");

  const { data: fin }          = useReportFinancial() as any;
  const { data: cashflow = [] } = useReportCashflow({ months:6 }) as any;
  const { data: risk     = [] } = useReportRisk()    as any;
  const { data: members  = [] } = useAdminMembers()  as any;

  const TABS = [
    { key:"financial", label:"📊 Financial" },
    { key:"members",   label:"👥 Members"   },
    { key:"loans",     label:"🤝 Loans"     },
    { key:"risk",      label:"⚠️ Risk"      },
  ];

  const {savings={}, loans={}, fin_members={}, withdraws={}} = fin || {};

  const totalSaved     = Number(savings?.[0]?.total_savings ||0); // Number(fin?.savings?.total_savings||0);
  const totalDisbursed = Number(loans?.[0]?.total_disbursed || 0); //Number(fin?.loans?.total_disbursed||0);
  const totalRepaid    = Number(loans?.[0]?.total_repaid || 0); //Number(fin?.loans?.total_repaid||0);
  const totalOverdue   = Number(loans?.[0]?.overdue_amount || 0); //Number(fin?.loans?.overdue_amount||0);
  const totalWithdraws = Number(withdraws?.[0]?.withdrawals || 0);
  const totalLoanAndInterest = Number(loans?.[0]?.loan_and_interest || 0);
  const totalOutstanding = totalLoanAndInterest - totalRepaid;
  const recoveryRate   = totalDisbursed ? Math.round((totalRepaid/totalDisbursed)*100) : 0;

  return (
    <div>
      <div className="flex justify-between items-center mb-5 flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap">
          {TABS.map(t=>(
            <Button key={t.key} size="sm" variant={tab===t.key?"primary":"secondary"} onClick={()=>setTab(t.key)}>{t.label}</Button>
          ))}
        </div>
        <div className="flex gap-2">
          <Select label="" value={period} onChange={setPeriod} options={[
            {value:"may-2025",label:"May 2025"},{value:"apr-2025",label:"Apr 2025"},
            {value:"q1-2025", label:"Q1 2025"}, {value:"ytd",     label:"Year to Date"},
          ]}/>
          <Button size="sm" variant="outline">⬇ Export CSV</Button>
          <Button size="sm" variant="outline">🖨 PDF</Button>
        </div>
      </div>

      {/* Report header */}
      <Card className="mb-4 bg-slate-900 border-slate-700">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-lg font-medium text-white">Heritage Cooperative Platform Report</p>
            <p className="text-sm text-slate-400 mt-1">Period: {period.toUpperCase()} · {new Date().toLocaleDateString()}</p>
          </div>
          <span className="text-sm text-emerald-400 font-medium">Heritage Co-op Admin</span>
        </div>
      </Card>

      {tab==="financial" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatCard label="Funds Under Mgmt"  value={fmt(totalSaved)}       sub="Member balances"    color="text-emerald-600"/>
            <StatCard label="Total Loan Disbursed"    value={fmt(totalDisbursed)}   sub="All loans issued"   color="text-blue-600"/>
            <StatCard label="Total Collected"    value={fmt(totalRepaid)}      sub={`${recoveryRate}% recovery`} color="text-emerald-600"/>
            <StatCard label="Outstanding"        value={fmt(totalOutstanding)}  sub="Remaining principal" color="text-violet-600"/>
            <StatCard label="Overdue Amount"     value={fmt(totalOverdue)}     sub="Past due date"      color="text-red-600"/>
            {/* <StatCard label="Net Reserve"        value={fmt(totalSaved-totalOutstanding)} sub="Savings minus exposure" color="text-slate-700"/> */}
            <StatCard label="Total Withdrawals"  value={fmt(totalWithdraws)} sub="Withdrawals done" color="text-slate-700"/>
          </div>
          <Card className="mb-4">
            <p className="text-sm font-medium text-slate-700 mb-4">Monthly cash flow</p>
            <MiniBarChart
              data={cashflow.length ? cashflow.map((r:any)=>({
                month: new Date(r.month).toLocaleString("default",{month:"short"}),
                contributions: Number(r.contributions), repayments: Number(r.repayments), withdrawals: Number(r.withdrawals)
              })) : [{month:"Jan",contributions:42000,repayments:4000,withdrawals:1200},{month:"Feb",contributions:47500,repayments:5000,withdrawals:800},{month:"Mar",contributions:51000,repayments:6500,withdrawals:900},{month:"Apr",contributions:55500,repayments:7000,withdrawals:1100},{month:"May",contributions:61200,repayments:8000,withdrawals:1500}]}
              keys={["contributions","repayments","withdrawals"]}
              colors={["#10b981","#8b5cf6","#f59e0b"]}
              height={160}
            />
          </Card>
          <Card>
            <p className="text-sm font-medium text-slate-700 mb-4">Key ratios</p>
            <div className="grid grid-cols-2 gap-3">
              {[
                {label:"Loan-to-savings ratio", value:`${totalSaved?Math.round((totalDisbursed/totalSaved)*100):0}%`, note:"Disbursed vs savings pool"},
                {label:"Recovery rate",         value:`${recoveryRate}%`,                                              note:"Repaid vs disbursed"},
                {label:"Default exposure",      value:`${totalDisbursed?Math.round((totalOverdue/totalDisbursed)*100):0}%`, note:"Overdue vs disbursed"},
                {label:"Active loans",          value:Number(loans?.[0]?.active_loans||0),                             note:"Currently outstanding"},
              ].map(r=>(
                <div key={r.label} className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs text-slate-500 mb-1">{r.label}</p>
                  <p className="text-xl font-medium text-slate-800 mb-0.5">{r.value}</p>
                  <p className="text-xs text-slate-400">{r.note}</p>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {tab==="members" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <StatCard label="Total Members"  value={Number(fin_members?.[0]?.total||0)}       color="text-blue-600"/>
            <StatCard label="Active"         value={Number(fin_members?.[0]?.active||0)}      color="text-emerald-600"/>
            <StatCard label="KYC Verified"   value={Number(fin_members?.[0]?.kyc_verified||0)}color="text-emerald-600"/>
            <StatCard label="KYC Pending"    value={Number(fin_members?.[0]?.kyc_pending||0)} color="text-amber-600"/>
            <StatCard label="Suspended"      value={Number(fin_members?.[0]?.suspended||0)}   color="text-red-600"/>
            <StatCard label="Avg Balance"    value={fin_members?.[0]?.active ? fmt(totalSaved/Number(fin_members?.[0]?.active)) : "$0"} color="text-violet-600"/>
          </div>
          <Card className="p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200"><p className="text-sm font-medium text-slate-700">Top savers</p></div>
            <table className="w-full border-collapse">
              <thead><tr className="bg-slate-50">
                {["Member","Balance","Contributions","Loans"].map(h=>(
                  <th key={h} className="px-4 py-2.5 text-left text-xs font-medium text-slate-500 border-b border-slate-200">{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {[...members].filter((m:any)=>m.status==="active").sort((a:any,b:any)=>b.balance-a.balance).slice(0,8).map((m:any)=>(
                  <tr key={m.id} className="border-b border-slate-100">
                    <td className="px-4 py-2.5 text-sm text-slate-800">{m.first_name} {m.last_name}</td>
                    <td className="px-4 py-2.5 text-sm font-medium text-emerald-600">{fmt(m.balance||0)}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{m.contribution_count||0}</td>
                    <td className="px-4 py-2.5 text-sm text-slate-600">{m.loan_count||0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab==="loans" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Total Disbursed"   value={totalDisbursed} sub="Loans disbursed" color="text-emerald-600"/>
            <StatCard label="Pending Request"  value={risk.filter((l:any)=>l.risk_score<45).length} sub="Requests pending" color="text-red-600"/>
            <StatCard label="Total Repaid"  value={totalRepaid} sub="Loans and interest paid" color="text-emerald-600"/>
            <StatCard label="Interest on Loans"    value={risk.filter((l:any)=>l.status==="overdue").length} sub="Interest Amount" color="text-amber-600"/>
          </div>
          
        </>
      )}

      {tab==="risk" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <StatCard label="Avg Risk Score"   value={risk.length?Math.round(risk.reduce((s:number,l:any)=>s+(l.risk_score||60),0)/risk.length):0} sub="higher = safer" color="text-emerald-600"/>
            <StatCard label="High Risk Loans"  value={risk.filter((l:any)=>l.risk_score<45).length} sub="Score below 45" color="text-red-600"/>
            <StatCard label="Surety Coverage"  value="100%" sub="All loans guaranteed" color="text-emerald-600"/>
            <StatCard label="Overdue Loans"    value={risk.filter((l:any)=>l.status==="overdue").length} sub="Past due date" color="text-amber-600"/>
          </div>
          <Card>
            <p className="text-sm font-medium text-slate-700 mb-4">Loan risk matrix</p>
            {(risk.length ? risk : [{id:"L001",borrower:"Alice Mwangi",purpose:"Business expansion",amount:5000,amount_repaid:2000,status:"active",risk_score:72},{id:"L002",borrower:"Eva Nkosi",purpose:"Equipment",amount:8000,amount_repaid:1000,status:"active",risk_score:61},{id:"L003",borrower:"Eva Nkosi",purpose:"School fees",amount:2000,amount_repaid:0,status:"overdue",risk_score:34}]).map((l:any)=>(
              <div key={l.id} className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
                <div className="w-14 text-xs font-medium text-slate-500 flex-shrink-0">{typeof l.id==="string"?l.id.slice(0,8):l.id}…</div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1.5">
                    <span className="text-sm text-slate-700 truncate">{l.borrower} — {l.purpose}</span>
                  </div>
                  <RiskMeter score={l.risk_score||60}/>
                  <p className="text-xs text-slate-400 mt-1">
                    {(l.risk_score||60)>70?"Low risk — strong history":(l.risk_score||60)>45?"Medium risk — monitor closely":"High risk — consider action"}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-slate-800">{fmt(l.amount-l.amount_repaid)}</p>
                  <p className="text-xs text-slate-400">outstanding</p>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
